import {
  MultiselectTypeaheadInput,
  Option,
} from '@rsa-digital/evo-shared-components/components/Form/MultiselectTypeaheadInput';
import ListWithPagination from '@rsa-digital/evo-shared-components/components/ListWithPagination';
import { generateKeyForElement } from '@rsa-digital/evo-shared-components/helpers/elementKeyHelper';
import { graphql } from 'gatsby';
import { camelCase } from 'lodash';
import React, { useState } from 'react';
import DocumentDownloadLinkBlock, {
  DocumentDownloadLinkBlockData,
} from 'components/DocumentDownloadLink';
import { navigate } from 'helpers/navigation';
import useOptimizelyVariation from 'helpers/useOptimizelyVariation';
import { DocumentDownloadLinkWrapper, FilterWrapper } from './styles';
import { fixedHeaderHeight } from '../constants';

type DocumentDownloadLinkListWithPaginationBlockProps = {
  document_download_link_list_with_pagination: {
    document_download_links: DocumentDownloadLinkBlockData[];
    filter_documents: boolean;
    filter_options: Option[];
    filter_placeholder_text: string;
  };
  currentPage?: number;
};

export const query = graphql`
  fragment DocumentDownloadLinkListWithPaginationBlock on cs__document_download_link_list_with_pagination {
    document_download_links {
      document_name
      document_url
      file_type
      file_size
      open_in_new_tab
      hide_icon
      description_text
    }
    filter_documents
    filter_options {
      label
    }
    filter_placeholder_text
  }
`;

const DocumentDownloadLinkListWithPaginationBlock: React.FC<
  DocumentDownloadLinkListWithPaginationBlockProps
> = ({ document_download_link_list_with_pagination, currentPage }) => {
  const enableFilterOnDocumentsExperiment = useOptimizelyVariation(
    process.env.GATSBY_OPTIMIZELY_FILTER_DOCUMENTS_EXPERIMENT
  );
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<Option[]>([]);

  const hasPredefinedFilterOptions =
    document_download_link_list_with_pagination.filter_options?.length > 0;

  const hasFilterDocumentsEnabled =
    enableFilterOnDocumentsExperiment &&
    document_download_link_list_with_pagination.filter_documents;

  const showFilter = hasFilterDocumentsEnabled || hasPredefinedFilterOptions;

  const documentOptions = hasFilterDocumentsEnabled
    ? document_download_link_list_with_pagination.document_download_links.map(
        (entry) => ({
          id: camelCase(entry.document_name),
          label: entry.document_name,
        })
      )
    : [];

  const filterOptions = [
    ...documentOptions,
    ...document_download_link_list_with_pagination.filter_options,
  ];

  const onPageChange = (selectedPage: number): void => {
    if (currentPage === selectedPage) {
      return;
    }
    navigate('./', { state: { page: selectedPage }, replace: true });
  };

  const filterCard = (relatedContentCard: DocumentDownloadLinkBlockData): boolean =>
    selectedFilterOptions
      .map((filter) => filter.label)
      .some((filter) => {
        return (
          relatedContentCard.document_name
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()) ||
          (relatedContentCard.description_text &&
            relatedContentCard.description_text
              .toLocaleLowerCase()
              .includes(filter.toLocaleLowerCase()))
        );
      });

  return (
    <>
      {showFilter && (
        <FilterWrapper>
          <MultiselectTypeaheadInput
            id={generateKeyForElement(document_download_link_list_with_pagination)}
            placeholder={
              document_download_link_list_with_pagination.filter_placeholder_text
            }
            options={filterOptions}
            selectedOptions={selectedFilterOptions}
            setSelectedOptions={setSelectedFilterOptions}
            hideOptionsByDefault={hasFilterDocumentsEnabled}
            data-cy="Document Download Link List with Pagination Filter"
          />
        </FilterWrapper>
      )}
      <ListWithPagination
        itemsToDisplayCount={10}
        fixedHeaderOffset={fixedHeaderHeight}
        initialPage={currentPage}
        onPageChange={onPageChange}
        data-cy="Document Download Link List with Pagination"
        displayDividers
        enforceBottomSpaceConsistency
        customBottomSpacing={{
          tabletLandscape: 4,
          mobile: 3,
        }}>
        {document_download_link_list_with_pagination.document_download_links
          .filter((entry) => (selectedFilterOptions.length ? filterCard(entry) : true))
          .map((entry) => (
            <DocumentDownloadLinkWrapper
              key={generateKeyForElement(entry)}
              data-cy="Document Download Link List with Pagination Link">
              <DocumentDownloadLinkBlock document_download_link={{ ...entry }} />
            </DocumentDownloadLinkWrapper>
          ))}
      </ListWithPagination>
    </>
  );
};

export default React.memo(DocumentDownloadLinkListWithPaginationBlock);
